import { Button } from '@/components/ui/button';
import {
  type VerificationDecisionInput,
  type VerificationFinding,
  type VerificationResult,
  type VerificationState,
  recordVerificationDecisions,
} from '@/hooks/useSummaryVerification';
import { cn } from '@/lib/utils';
import { AlertTriangle, Check, Loader2, ShieldCheck, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

const SEVERITY_LABEL: Record<string, string> = {
  severe: 'alvarlegt',
  moderate: 'miðlungs',
  mild: 'vægt',
};

const SEVERITY_ORDER: Record<string, number> = { severe: 0, moderate: 1, mild: 2 };

interface Props {
  state: VerificationState;
  result: VerificationResult | null;
  /** Current summary text the findings were produced against. */
  summary: string | null;
  /** Session/audio GUID the decisions are persisted against. */
  audioId: string | null;
  /** Apply an edited summary (same path as a manual edit). */
  onApply?: (newSummary: string) => void;
  /** Icon and count only, for the composer panel's toolbar. */
  compact?: boolean;
}

/** A finding can be auto-applied only when its 'original' snippet is present verbatim in the summary. */
const canApply = (finding: VerificationFinding, summary: string | null): boolean =>
  !!summary && finding.original.length > 0 && summary.includes(finding.original);

export const SummaryVerification = ({ state, result, summary, audioId, onApply, compact = false }: Props) => {
  const [open, setOpen] = useState(false);
  // Findings the user has handled (accepted or rejected) — removed from the list.
  const [handled, setHandled] = useState<Set<string>>(new Set());

  // A new verification run replaces the findings; reset handled state.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset when the result identity changes
  useEffect(() => {
    setHandled(new Set());
  }, [result]);

  // Only surface findings we can actually apply (their 'original' snippet is present
  // verbatim in the summary). Non-applicable ones (e.g. pure omissions) are filtered out.
  const findings = useMemo(
    () =>
      [...(result?.findings ?? [])]
        .filter((f) => canApply(f, summary))
        .sort((a, b) => (SEVERITY_ORDER[a.severity] ?? 9) - (SEVERITY_ORDER[b.severity] ?? 9)),
    [result, summary]
  );

  const keyFor = (f: VerificationFinding, i: number) => `${i}:${f.description}`;
  const visible = findings.filter((f, i) => !handled.has(keyFor(f, i)));

  if (state === 'idle') return null;
  // Compact shows the icon only; the words stay for screen readers and as a tooltip.
  const label = (text: string) => (compact ? <span className="sr-only">{text}</span> : text);
  const tooltip = (text: string) => (compact ? text : undefined);

  if (state === 'verifying') {
    return (
      <span className="flex items-center gap-1.5 text-muted-foreground text-sm" title={tooltip('Fer yfir samantekt…')}>
        <Loader2 className="h-4 w-4 animate-spin" />
        {label('Fer yfir samantekt…')}
      </span>
    );
  }

  if (state === 'error') {
    return null;
  }

  // state === 'done'
  if (visible.length === 0) {
    return (
      <span className="flex items-center gap-1.5 text-green-600 text-sm" title={tooltip('Samantekt staðfest')}>
        <ShieldCheck className="h-4 w-4" />
        {label('Samantekt staðfest')}
      </span>
    );
  }

  const dismiss = (f: VerificationFinding, i: number) => {
    setHandled((prev) => new Set(prev).add(keyFor(f, i)));
  };

  const accept = (f: VerificationFinding, i: number) => {
    const willApply = !!onApply && !!summary && canApply(f, summary);
    if (willApply && onApply && summary) {
      onApply(summary.replace(f.original, f.suggested_fix));
    }
    if (audioId) {
      recordVerificationDecisions(audioId, [{ finding_key: f.finding_key, decision: 'accepted', applied: willApply }]);
    }
    dismiss(f, i);
  };

  const reject = (f: VerificationFinding, i: number) => {
    if (audioId) {
      recordVerificationDecisions(audioId, [{ finding_key: f.finding_key, decision: 'rejected', applied: false }]);
    }
    dismiss(f, i);
  };

  const applyAll = () => {
    if (!onApply || !summary) return;
    let next = summary;
    const nowHandled = new Set(handled);
    const decisions: VerificationDecisionInput[] = [];
    for (const f of visible) {
      // index in the full `findings` array, so keys stay stable
      const fullIndex = findings.indexOf(f);
      // Re-check against the progressively-mutated `next` (not the original summary via
      // canApply): an earlier replacement may have consumed an overlapping snippet, so a
      // later finding can legitimately fail to apply — recorded as accepted/applied:false.
      const willApply = next.includes(f.original);
      if (willApply) {
        next = next.replace(f.original, f.suggested_fix);
      }
      decisions.push({ finding_key: f.finding_key, decision: 'accepted', applied: willApply });
      nowHandled.add(keyFor(f, fullIndex));
    }
    if (next !== summary) onApply(next);
    if (audioId && decisions.length) recordVerificationDecisions(audioId, decisions);
    setHandled(nowHandled);
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'flex cursor-pointer items-center gap-1.5 text-amber-600 text-sm hover:text-amber-700',
          compact && 'h-8 rounded-md px-2 hover:bg-amber-50'
        )}
        aria-expanded={open}
        aria-label={`Villur í samantekt: ${visible.length}`}
      >
        {compact ? (
          <>
            <AlertTriangle className="h-4 w-4" />
            <span className="font-medium">{visible.length}</span>
          </>
        ) : (
          <>
            <span className="relative inline-flex">
              <AlertTriangle className="h-4 w-4" />
              <span className="-right-2 -top-2 absolute flex h-4 min-w-4 items-center justify-center rounded-full bg-amber-600 px-1 font-semibold text-[10px] text-white leading-none">
                {visible.length}
              </span>
            </span>
            <span className="ml-1.5">Villur í samantekt</span>
          </>
        )}
      </button>
      {open && (
        <div className="absolute right-0 z-20 mt-2 w-[28rem] rounded-lg border bg-card p-3 shadow-md">
          <ul className="space-y-3">
            {visible.map((f) => {
              const fullIndex = findings.indexOf(f);
              return (
                <li key={keyFor(f, fullIndex)} className="text-sm">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <span className="font-medium text-amber-700">[{SEVERITY_LABEL[f.severity] ?? f.severity}]</span>{' '}
                      {f.description}
                      <div className="mt-1 rounded bg-muted/60 p-1.5 text-muted-foreground text-xs">
                        <span className="line-through opacity-70">{f.original}</span>
                        <span className="mx-1">→</span>
                        <span className="text-foreground">{f.suggested_fix}</span>
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 text-muted-foreground hover:bg-red-50 hover:text-red-600"
                        aria-label="Hafna lagfæringu"
                        title="Hafna"
                        onClick={() => reject(f, fullIndex)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 text-green-600 hover:bg-green-50 hover:text-green-700"
                        aria-label="Samþykkja lagfæringu"
                        title="Samþykkja"
                        onClick={() => accept(f, fullIndex)}
                      >
                        <Check className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
          {visible.length > 1 && (
            <div className="mt-3 flex justify-end border-t pt-2">
              <Button type="button" size="sm" variant="ghost" className="h-7 gap-1" onClick={applyAll}>
                <Check className="h-4 w-4" /> Laga allt ({visible.length})
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
